<template>
  <div class="docs-governance-addon">
    <div class="addon-head">
      <div>
        <span>HRM_DOCS_GOVERNANCE</span>
        <h2>文件资料治理洞察</h2>
        <p>聚合文件分类、可见范围、上传人、大小和指纹信息，识别孤立文件、重复文件、分类缺口和公开风险。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">上传人资料质量排行</div>
          <el-table :data="overview.ownerQualityRanking" size="small" v-loading="loading">
            <el-table-column prop="staff_name" label="上传人" width="120" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="docs_count" label="文件数" width="80" />
            <el-table-column prop="total_size_kb" label="大小KB" width="90" />
            <el-table-column prop="missing_category_count" label="分类缺口" width="90" />
            <el-table-column prop="public_count" label="公开" width="70" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">可见范围分布</div>
          <div class="status-list">
            <div v-for="item in overview.visibilityDistribution" :key="item.visibility_name" class="status-item">
              <span>{{ formatVisibility(item.visibility_name) }}</span>
              <strong>{{ item.docs_count }}</strong>
              <em>{{ item.total_size_kb || 0 }} KB</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">重复文件指纹</div>
          <el-table :data="overview.duplicateMd5List" size="small" v-loading="loading" empty-text="暂无重复">
            <el-table-column prop="md5" label="MD5" min-width="180" />
            <el-table-column prop="duplicate_count" label="重复数" width="80" />
            <el-table-column prop="total_size_kb" label="大小KB" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">孤立文件</div>
          <el-table :data="overview.orphanDocsList" size="small" v-loading="loading" empty-text="暂无孤立文件">
            <el-table-column prop="old_name" label="原文件名" min-width="160" />
            <el-table-column prop="type" label="类型" width="70" />
            <el-table-column prop="size" label="大小KB" width="90" />
            <el-table-column prop="staff_id" label="上传人ID" width="90" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">分类分布</div>
          <div class="status-list compact">
            <div v-for="item in overview.categoryDistribution" :key="item.category_name" class="status-item">
              <span>{{ item.category_name }}</span>
              <strong>{{ item.docs_count }}</strong>
              <em>{{ item.total_size_kb || 0 }} KB</em>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">月度上传趋势</div>
          <div class="status-list compact">
            <div v-for="item in overview.monthlyUploadTrend" :key="item.month_label" class="status-item">
              <span>{{ item.month_label }}</span>
              <strong>{{ item.docs_count }}</strong>
              <em>{{ item.total_size_kb || 0 }} KB</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getDocsGovernanceOverview } from '../api/docsGovernance'

export default {
  name: 'DocsGovernanceOverview',
  data () {
    return {
      loading: false,
      overview: {
        docsCount: 0,
        deletedDocsCount: 0,
        totalSizeKb: 0,
        missingOwnerCount: 0,
        missingCategoryCount: 0,
        privateDocsCount: 0,
        publicDocsCount: 0,
        duplicateMd5GroupCount: 0,
        typeDistribution: [],
        categoryDistribution: [],
        visibilityDistribution: [],
        ownerQualityRanking: [],
        duplicateMd5List: [],
        orphanDocsList: [],
        zeroSizeDocsList: [],
        monthlyUploadTrend: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '有效文件', value: this.overview.docsCount, meta: `${this.overview.totalSizeKb || 0} KB` },
        { label: '分类缺口', value: this.overview.missingCategoryCount, meta: `${this.overview.deletedDocsCount || 0} 个已删除`, tone: 'orange' },
        { label: '孤立文件', value: this.overview.missingOwnerCount, meta: '上传人无效', tone: 'red' },
        { label: '公开/重复风险', value: this.overview.publicDocsCount, meta: `${this.overview.duplicateMd5GroupCount || 0} 组重复`, tone: 'blue' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getDocsGovernanceOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '文件资料治理洞察加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    formatVisibility (value) {
      const map = { PUBLIC: '公开', INTERNAL: '内部', PRIVATE: '私有', 未设置: '未设置' }
      return map[value] || value
    }
  }
}
</script>

<style lang="less" scoped>
.docs-governance-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #075985;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.status-list {
  display: grid;
  gap: 10px;
}

.status-list.compact {
  max-height: 320px;
  overflow: auto;
}

.status-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.status-item span,
.status-item em {
  color: #5f6f89;
  font-style: normal;
}

.status-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
